import { useCombobox } from 'downshift';
import { useMemo, type ChangeEvent, type CSSProperties, type InputHTMLAttributes } from 'react';
import { AutocompleteContainer, AutocompleteMenu, AutocompleteResults, AutocompleteWrapper } from './styles';

type AutocompleteItem = {
  id?: string;
  label: string;
};

export type AutocompleteProps<T extends AutocompleteItem> = {
  style?: CSSProperties;
  isCentered: boolean;
  invalid?: boolean;
  id?: string;
  items: T[];
  getItemValue: (item: T) => string;
  shouldItemRender: (item: T, value: string) => boolean;
  value: string | undefined;
  onChange: (e: ChangeEvent<HTMLInputElement>, value: string) => void;
  onSelect: (value: string) => void;
  sortItems?: (a: T, b: T) => number;
  menuStyle?: CSSProperties;
  inputProps?: InputHTMLAttributes<HTMLInputElement> & { 'data-testid'?: string };
};

function getItemKey<T extends AutocompleteItem>(item: T, getItemValue: (item: T) => string, index: number) {
  return item.id ?? `${getItemValue(item)}-${index}`;
}

/**
 * Inline free-text combobox for Ava forms. The value is a controlled `string`; users may type
 * any text, with optional suggestions filtered via `shouldItemRender` and chosen via `onSelect`.
 *
 * Use for fields like group names or external IDs where free-form input is allowed. For
 * select-from-list controls (object selection, fuzzy search, portaled dropdowns), use Kyoko
 * `SearchSelect` instead.
 */
export function Autocomplete<T extends AutocompleteItem>({
  style = {},
  isCentered,
  invalid,
  id,
  items,
  getItemValue,
  shouldItemRender,
  value,
  onChange,
  onSelect,
  sortItems,
  menuStyle,
  inputProps = {},
}: AutocompleteProps<T>) {
  const inputValue = value ?? '';

  const filteredItems = useMemo(() => {
    const matchingItems = items.filter(item => shouldItemRender(item, inputValue));
    if (sortItems == null) {
      return matchingItems;
    }
    return matchingItems.toSorted(sortItems);
  }, [items, shouldItemRender, sortItems, inputValue]);

  const { isOpen, getMenuProps, getInputProps, highlightedIndex, getItemProps } = useCombobox<T>({
    items: filteredItems,
    inputValue,
    itemToString: item => (item ? getItemValue(item) : ''),
    onInputValueChange: () => {
      // Controlled value updates are propagated via the input onChange handler below.
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem != null) {
        onSelect(getItemValue(selectedItem));
      }
    },
    // Free-text fields must not select the highlighted suggestion on Tab blur.
    // Matches former react-autocomplete selectOnBlur=false behavior.
    stateReducer: (state, { type, changes }) => {
      if (type === useCombobox.stateChangeTypes.InputBlur) {
        return {
          ...changes,
          selectedItem: state.selectedItem,
          inputValue: state.inputValue,
        };
      }
      return changes;
    },
  });

  const showMenu = isOpen && filteredItems.length > 0 && !inputProps.disabled;

  const downshiftInputProps = getInputProps({
    ...inputProps,
    id: inputProps.id ?? id,
    className: [inputProps.className, invalid ? 'invalid' : undefined].filter(Boolean).join(' ') || undefined,
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      inputProps.onChange?.(e);
      onChange(e, e.currentTarget.value);
    },
  });

  return (
    <AutocompleteWrapper style={style} isCentered={isCentered}>
      <AutocompleteContainer>
        <input {...downshiftInputProps} />
        <AutocompleteMenu
          {...getMenuProps({}, { suppressRefError: !showMenu })}
          style={{ ...menuStyle, display: showMenu ? undefined : 'none' }}
        >
          {filteredItems.map((item, index) => (
            <AutocompleteResults
              key={getItemKey(item, getItemValue, index)}
              isHighlighted={highlightedIndex === index}
              data-testid="autocomplete-result-item"
              {...getItemProps({ item, index })}
            >
              {getItemValue(item)}
            </AutocompleteResults>
          ))}
        </AutocompleteMenu>
      </AutocompleteContainer>
    </AutocompleteWrapper>
  );
}

export function sortAutocompleteItemsFn<T extends { label: string }>(a: T, b: T) {
  return a.label.localeCompare(b.label);
}
